/**
 * Toggle "Alertas de atividade neste aparelho" — vive no Perfil porque é
 * escolha da pessoa por aparelho, não da viagem. O que merece aviso (e com
 * quanta antecedência) é decidido na atividade, por quem organiza.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { desligarAlertas, type EstadoDosAlertas, estadoDosAlertas, ligarAlertas, precisaInstalarNoIos } from './push';

async function lerConfig(): Promise<{ push: { publicKey: string } | null }> {
  const res = await fetch('/api/v1/config', { credentials: 'same-origin' });
  if (!res.ok) return { push: null };
  return (await res.json()) as { push: { publicKey: string } | null };
}

export function AlertasNesteAparelho() {
  const config = useQuery({ queryKey: ['config'], queryFn: lerConfig, staleTime: Infinity });
  const [estado, setEstado] = useState<EstadoDosAlertas | 'carregando'>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [mexendo, setMexendo] = useState(false);

  useEffect(() => {
    void estadoDosAlertas().then(setEstado).catch(() => setEstado('indisponivel'));
  }, []);

  const publicKey = config.data?.push?.publicKey ?? null;
  const servidorSem = config.isSuccess && !publicKey;

  async function alternar() {
    if (!publicKey || mexendo) return;
    setMexendo(true);
    setErro(null);
    try {
      if (estado === 'ligado') {
        await desligarAlertas();
        setEstado('desligado');
      } else {
        setEstado(await ligarAlertas(publicKey));
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível mudar os alertas.');
    } finally {
      setMexendo(false);
    }
  }

  const ligado = estado === 'ligado';
  return (
    <div className="alertas">
      <div className="alertas__linha">
        <span className="mapa__pessoa-texto">
          <span className="mapa__pessoa-nome">Alertas de atividade neste aparelho</span>
          <span className="mapa__pessoa-familia">
            {servidorSem
              ? 'Não está ligado neste ambiente.'
              : estado === 'indisponivel'
                ? precisaInstalarNoIos()
                  ? 'No iPhone, adicione o app à Tela de Início para receber alertas.'
                  : 'Este navegador não oferece notificações ao app.'
                : estado === 'negado'
                  ? 'O navegador bloqueou notificações para este site. Libere nas configurações do site.'
                  : ligado
                    ? 'Você recebe um aviso antes das atividades que têm alerta.'
                    : 'Receba um aviso antes das atividades que têm alerta.'}
          </span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={ligado}
          aria-label="Alertas de atividade neste aparelho"
          className={`mapa__toggle${ligado ? ' mapa__toggle--on' : ''}`}
          disabled={servidorSem || estado === 'indisponivel' || estado === 'negado' || estado === 'carregando' || mexendo}
          onClick={() => void alternar()}
        >
          <span className="mapa__toggle-bola" />
        </button>
      </div>
      {erro && <p className="auth-error" role="alert">{erro}</p>}
    </div>
  );
}
